import { supabase } from "@/integrations/supabase/client";
import { detectSeriesFromTitle, validateSeriesData } from "@/lib/seriesUtils";
import { detectFromKnownPatterns } from "@/lib/knownSeriesPatterns";
import { toast } from "sonner";

/**
 * Normalize title for fuzzy matching
 */
function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/['']/g, "'")
    .replace(/[""]/g, '"')
    .replace(/[^\w\s'"-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract core title keywords for matching (removes common words)
 */
function extractTitleKeywords(title: string): string[] {
  const stopWords = ['the', 'a', 'an', 'and', 'or', 'of', 'in', 'on', 'at', 'to', 'for', 'by', 'with'];
  return normalizeTitle(title)
    .split(' ')
    .filter(word => word.length > 2 && !stopWords.includes(word));
}

/**
 * Infer series data from similar editions in the database
 */
async function inferSeriesFromSimilarEditions(
  bookId: string,
  title: string,
  authors: string[] | null
): Promise<{ seriesName: string; position: number; totalBooks?: number } | null> {
  try {
    // First, try known patterns
    const patternMatch = detectFromKnownPatterns(title);
    if (patternMatch) {
      console.log(`[Series Inference] Found pattern match for "${title}": ${patternMatch.seriesName} #${patternMatch.position}`);
      return patternMatch;
    }

    // Extract keywords for fuzzy search
    const keywords = extractTitleKeywords(title);
    if (keywords.length < 2) {
      return null;
    }

    // Build a search query using the most distinctive keywords
    const searchTerms = keywords.slice(0, 4).join(' & ');
    
    // Search for similar books that have series data
    const { data: similarBooks, error } = await supabase
      .from('books')
      .select('id, title, series_name, series_position, series_total, authors')
      .not('series_name', 'is', null)
      .textSearch('title_search', searchTerms, { type: 'websearch' })
      .limit(20);

    if (error || !similarBooks || similarBooks.length === 0) {
      // Fallback: try exact title match with series data
      const { data: exactMatches } = await supabase
        .from('books')
        .select('id, title, series_name, series_position, series_total, authors')
        .not('series_name', 'is', null)
        .ilike('title', `%${keywords.slice(0, 3).join('%')}%`)
        .limit(20);

      if (!exactMatches || exactMatches.length === 0) {
        return null;
      }
      
      // Use exact matches instead
      return findBestSeriesMatch(title, authors, exactMatches);
    }

    return findBestSeriesMatch(title, authors, similarBooks);
  } catch (error) {
    console.error('[Series Inference] Error:', error);
    return null;
  }
}

/**
 * Find the best series match from a list of similar books
 */
function findBestSeriesMatch(
  targetTitle: string,
  targetAuthors: string[] | null,
  candidates: any[]
): { seriesName: string; position: number; totalBooks?: number } | null {
  const normalizedTarget = normalizeTitle(targetTitle);
  const targetAuthorNorm = targetAuthors?.[0]?.toLowerCase().trim();

  let bestMatch: { seriesName: string; position: number; totalBooks?: number; score: number } | null = null;

  for (const candidate of candidates) {
    if (!candidate.series_name || !candidate.series_position) continue;

    const normalizedCandidate = normalizeTitle(candidate.title);
    const candidateAuthor = candidate.authors?.[0]?.toLowerCase().trim();

    // Calculate similarity score
    let score = 0;

    // Title similarity (simple word overlap)
    const targetWords = new Set(normalizedTarget.split(' '));
    const candidateWords = normalizedCandidate.split(' ');
    const overlap = candidateWords.filter(w => targetWords.has(w)).length;
    score += (overlap / Math.max(targetWords.size, candidateWords.length)) * 50;

    // Author match bonus
    if (targetAuthorNorm && candidateAuthor) {
      if (targetAuthorNorm === candidateAuthor) {
        score += 40;
      } else if (targetAuthorNorm.includes(candidateAuthor) || candidateAuthor.includes(targetAuthorNorm)) {
        score += 25;
      }
    }

    // Exact title match bonus
    if (normalizedTarget === normalizedCandidate) {
      score += 50;
    }

    if (score > 50 && (!bestMatch || score > bestMatch.score)) {
      bestMatch = {
        seriesName: candidate.series_name,
        position: candidate.series_position,
        totalBooks: candidate.series_total,
        score
      };
    }
  }

  if (bestMatch) {
    console.log(`[Series Inference] Found match for "${targetTitle}": ${bestMatch.seriesName} #${bestMatch.position} (score: ${bestMatch.score})`);
    return {
      seriesName: bestMatch.seriesName,
      position: bestMatch.position,
      totalBooks: bestMatch.totalBooks
    };
  }

  return null;
}

export async function backfillSeriesFromTitles(batchSize: number = 100) {
  try {
    console.log('[Backfill Series] Starting series detection from titles...');
    
    // Get books without series data
    const { data: booksWithoutSeries, error: fetchError } = await supabase
      .from('books')
      .select('id, title, authors')
      .is('series_name', null)
      .limit(batchSize);
    
    if (fetchError) throw fetchError;
    
    if (!booksWithoutSeries || booksWithoutSeries.length === 0) {
      console.log('[Backfill Series] No books without series data found');
      return { processed: 0, updated: 0 };
    }
    
    console.log(`[Backfill Series] Processing ${booksWithoutSeries.length} books...`);
    
    let updated = 0;
    const updates: { id: string; series_name: string; series_position: number; series_total?: number }[] = [];
    
    for (const book of booksWithoutSeries) {
      // Try title pattern detection first
      let seriesInfo = detectSeriesFromTitle(book.title);
      
      // If no match, try known patterns
      if (!seriesInfo) {
        const patternMatch = detectFromKnownPatterns(book.title);
        if (patternMatch) {
          seriesInfo = { seriesName: patternMatch.seriesName, position: patternMatch.position };
        }
      }
      
      if (seriesInfo) {
        updates.push({
          id: book.id,
          series_name: seriesInfo.seriesName,
          series_position: seriesInfo.position || 1
        });
        updated++;
      }
    }
    
    // Batch update books with series info
    if (updates.length > 0) {
      for (const update of updates) {
        const { error: updateError } = await supabase
          .from('books')
          .update({
            series_name: update.series_name,
            series_position: update.series_position,
            series_total: update.series_total
          })
          .eq('id', update.id);
        
        if (updateError) {
          console.error(`[Backfill Series] Error updating book ${update.id}:`, updateError);
        }
      }
    }
    
    console.log(`[Backfill Series] Updated ${updated} books with series info`);
    
    return {
      processed: booksWithoutSeries.length,
      updated
    };
  } catch (error) {
    console.error('[Backfill Series] Error:', error);
    throw error;
  }
}

export async function triggerSeriesEnrichment(bookIds?: string[]) {
  try {
    console.log('[Series Enrichment] Triggering enrichment...');
    
    const { data, error } = await supabase.functions.invoke('enrich-books', {
      body: {
        action: 'enrich-books',
        bookIds: bookIds || undefined,
        batchSize: bookIds ? Math.min(bookIds.length, 100) : 100,
        prioritizeIsbndb: true,
        extractSeries: true
      }
    });
    
    if (error) throw error;
    
    console.log('[Series Enrichment] Enrichment completed:', data);
    return data;
  } catch (error) {
    console.error('[Series Enrichment] Error:', error);
    throw error;
  }
}

/**
 * Enhanced backfill that infers series from similar editions
 */
export async function backfillUserSeriesTracking() {
  try {
    console.log('[Backfill User Series] Starting user series tracking backfill with inference...');
    
    // Get ALL user books that are read or reading (not just ones with series data)
    const { data: userBooks, error: fetchError } = await supabase
      .from('user_books')
      .select(`
        id,
        user_id,
        book_id,
        shelf,
        finished_at,
        books!inner (
          id,
          title,
          series_name,
          series_position,
          series_total,
          authors
        )
      `)
      .in('shelf', ['read', 'reading']);
    
    if (fetchError) throw fetchError;
    
    if (!userBooks || userBooks.length === 0) {
      console.log('[Backfill User Series] No user books found');
      return { processed: 0, inferred: 0 };
    }
    
    console.log(`[Backfill User Series] Processing ${userBooks.length} user books...`);
    
    // Group by user and series
    const seriesMap = new Map<string, {
      user_id: string;
      series_name: string;
      author_name: string;
      highest_position: number;
      total_books: number | null;
      last_book_id: string;
      last_read_at: string | null;
    }>();
    
    let inferredCount = 0;
    const booksToUpdate: { id: string; series_name: string; series_position: number; series_total?: number }[] = [];
    
    for (const ub of userBooks) {
      const book = (ub.books as any);
      if (!book) continue;
      
      let seriesName = book.series_name;
      let seriesPosition = book.series_position;
      let seriesTotal = book.series_total;
      
      // If book doesn't have series data, try to infer it
      if (!seriesName || !seriesPosition) {
        const inferred = await inferSeriesFromSimilarEditions(book.id, book.title, book.authors);
        
        if (inferred) {
          seriesName = inferred.seriesName;
          seriesPosition = inferred.position;
          seriesTotal = inferred.totalBooks;
          inferredCount++;
          
          // Queue the book for update with inferred series data
          booksToUpdate.push({
            id: book.id,
            series_name: seriesName,
            series_position: seriesPosition,
            series_total: seriesTotal
          });
          
          console.log(`[Backfill User Series] Inferred series for "${book.title}": ${seriesName} #${seriesPosition}`);
        }
      }
      
      if (!seriesName || !seriesPosition) continue;
      
      const normalizedSeries = seriesName.toLowerCase().trim();
      const primaryAuthor = book.authors?.[0] || 'Unknown';
      const key = `${ub.user_id}|${normalizedSeries}|${primaryAuthor.toLowerCase()}`;
      
      const existing = seriesMap.get(key);
      
      if (!existing || seriesPosition > existing.highest_position) {
        seriesMap.set(key, {
          user_id: ub.user_id,
          series_name: normalizedSeries,
          author_name: primaryAuthor,
          highest_position: seriesPosition,
          total_books: seriesTotal,
          last_book_id: ub.book_id,
          last_read_at: ub.finished_at
        });
      }
    }
    
    // Update books with inferred series data (enriches editions)
    if (booksToUpdate.length > 0) {
      console.log(`[Backfill User Series] Updating ${booksToUpdate.length} books with inferred series data...`);
      
      for (const update of booksToUpdate) {
        const { error: updateError } = await supabase
          .from('books')
          .update({
            series_name: update.series_name,
            series_position: update.series_position,
            series_total: update.series_total
          })
          .eq('id', update.id);
        
        if (updateError) {
          console.error(`[Backfill User Series] Error updating book ${update.id}:`, updateError);
        }
      }
    }
    
    // Insert or update tracking records
    for (const [_, tracking] of seriesMap) {
      const { error: upsertError } = await supabase
        .from('user_series_tracking')
        .upsert({
          user_id: tracking.user_id,
          series_name: tracking.series_name,
          series_name_display: tracking.series_name,
          author_name: tracking.author_name,
          highest_book_position: tracking.highest_position,
          total_books_known: tracking.total_books,
          last_book_id: tracking.last_book_id,
          last_book_read_at: tracking.last_read_at,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,series_name,author_name'
        });
      
      if (upsertError) {
        console.error('[Backfill User Series] Error upserting:', upsertError);
      }
    }
    
    console.log(`[Backfill User Series] Processed ${seriesMap.size} unique series, inferred ${inferredCount} books`);
    
    return {
      processed: seriesMap.size,
      inferred: inferredCount
    };
  } catch (error) {
    console.error('[Backfill User Series] Error:', error);
    throw error;
  }
}

/**
 * Clean up junk series data (editions, languages, etc. being mistaken as series)
 */
export async function cleanupJunkSeriesData(): Promise<{ cleaned: number }> {
  try {
    console.log('[Cleanup Series] Starting junk series cleanup...');
    
    // Blocklist patterns for false-positive series names
    const junkPatterns = [
      '%edition%', '%version%', '%anniversary%', '%revised%', '%updated%',
      '%expanded%', '%deluxe%', '%collector%', '%hardcover%', '%paperback%',
      '%reprint%', '%reissue%', '%illustrated%', '%unabridged%', '%abridged%',
      '%complete%', '%1st%', '%2nd%', '%3rd%', '%first%', '%second%', '%third%',
      '%persian%', '%spanish%', '%french%', '%german%', '%italian%', '%portuguese%',
      '%chinese%', '%japanese%', '%korean%', '%arabic%', '%russian%', '%hindi%',
      '%translation%', '%translated%', '%movie tie-in%', '%tie-in%', '%tv tie-in%',
      '%film edition%', '%international%', '%uk edition%', '%us edition%',
      '%american%', '%british%', '%canadian%', '%australian%',
      '%special%', '%limited%', '%signed%', '%numbered%', '%boxed%', '%box set%', '%omnibus%'
    ];
    
    let totalCleaned = 0;
    
    for (const pattern of junkPatterns) {
      const { data, error } = await supabase
        .from('books')
        .update({ series_name: null, series_position: null, series_total: null })
        .ilike('series_name', pattern)
        .select('id');
      
      if (!error && data) {
        totalCleaned += data.length;
        if (data.length > 0) {
          console.log(`[Cleanup Series] Cleared ${data.length} books matching pattern: ${pattern}`);
        }
      }
    }
    
    console.log(`[Cleanup Series] Total cleaned: ${totalCleaned} books`);
    return { cleaned: totalCleaned };
  } catch (error) {
    console.error('[Cleanup Series] Error:', error);
    throw error;
  }
}

/**
 * Get statistics about current series data
 */
export async function getSeriesStats(): Promise<{
  totalWithSeries: number;
  uniqueSeriesNames: number;
  booksWithPosition: number;
  booksWithTotal: number;
}> {
  try {
    const { count: totalWithSeries } = await supabase
      .from('books')
      .select('id', { count: 'exact', head: true })
      .not('series_name', 'is', null);
    
    const { count: booksWithPosition } = await supabase
      .from('books')
      .select('id', { count: 'exact', head: true })
      .not('series_position', 'is', null);
    
    const { count: booksWithTotal } = await supabase
      .from('books')
      .select('id', { count: 'exact', head: true })
      .not('series_total', 'is', null);
    
    // Get unique series count
    const { data: seriesData } = await supabase
      .from('books')
      .select('series_name')
      .not('series_name', 'is', null);
    
    const uniqueSeries = new Set(seriesData?.map(b => b.series_name?.toLowerCase()));
    
    return {
      totalWithSeries: totalWithSeries || 0,
      uniqueSeriesNames: uniqueSeries.size,
      booksWithPosition: booksWithPosition || 0,
      booksWithTotal: booksWithTotal || 0
    };
  } catch (error) {
    console.error('[Series Stats] Error:', error);
    return { totalWithSeries: 0, uniqueSeriesNames: 0, booksWithPosition: 0, booksWithTotal: 0 };
  }
}

export async function runCompleteSeriesBackfill() {
  try {
    // Step 0: Clean up junk series data first
    toast.info('Cleaning up junk series data...');
    const cleanupResult = await cleanupJunkSeriesData();
    console.log(`Step 0 complete: ${cleanupResult.cleaned} junk entries cleaned`);
    
    toast.info('Detecting series from titles and patterns...');
    
    // Step 1: Detect series from titles (handles explicitly numbered books)
    const titleResults = await backfillSeriesFromTitles(500);
    console.log(`Step 1 complete: ${titleResults.updated} books updated from titles`);
    
    // Step 2: Backfill user tracking with inference (the main improvement)
    toast.info('Inferring series from similar editions...');
    const trackingResults = await backfillUserSeriesTracking();
    console.log(`Step 2 complete: ${trackingResults.processed} series tracked, ${trackingResults.inferred} books inferred`);
    
    // Step 3: Trigger enrichment for remaining books (gets series from APIs)
    toast.info('Fetching additional series data from external sources...');
    const { data: userBookIds } = await supabase
      .from('user_books')
      .select('book_id')
      .in('shelf', ['read', 'reading']);
    
    if (userBookIds && userBookIds.length > 0) {
      const bookIds = userBookIds.map(ub => ub.book_id).slice(0, 100);
      await triggerSeriesEnrichment(bookIds);
      console.log(`Step 3 complete: Triggered enrichment for ${bookIds.length} books`);
    }
    
    const totalUpdates = titleResults.updated + (trackingResults.inferred || 0);
    toast.success(`Series detection complete! ${totalUpdates} books enriched, ${trackingResults.processed} series tracked, ${cleanupResult.cleaned} junk entries cleaned.`);
    
    return {
      booksUpdated: totalUpdates,
      seriesTracked: trackingResults.processed,
      booksInferred: trackingResults.inferred || 0,
      junkCleaned: cleanupResult.cleaned
    };
  } catch (error) {
    console.error('[Complete Backfill] Error:', error);
    toast.error('Series detection failed');
    throw error;
  }
}
